import * as SunCalc from "suncalc";
import { DateTime, Interval } from "luxon";
import Alpine from "alpinejs";
import { waitForAlpineStore } from "../waitForAlpineStore";

export function SandhyaKala() {
  return {
    solarTimings: null,
    sandhyaTimings: null,

    sandhyaKalaTimeNarration: {
      pratah_sandhyopasanam: {},
      madhyahnika_sandhyopasanam: {},
      sayam_sandhyopasanam: {},
    },

    // Example format: 2025-12-25 01:09 PM +05:30
    // timeFormat: "yyyy-MM-dd hh:mm a ZZ",
    timeFormat: "hh:mm a",

    init() {
      this.watchForChanges();
    },

    watchForChanges() {
      // 1. WATCHER: Only for Time Format (UI Preference)
      waitForAlpineStore("userPreferences").then((store) => {
        Alpine.effect(() => {
          const format = store.timeFormat === "24h" ? "HH:mm" : "hh:mm a";
          const seconds = store.showSeconds ? ":ss" : "";
          this.timeFormat = format.replace("mm", "mm" + seconds);
        });
      });

      // 2. WATCHER: Heavy Astronomical Computations
      // Runs ONLY when Latitude, Longitude, Timezone, or Date change
      Alpine.effect(() => {
        const loc = Alpine.store("location");
        const date = Alpine.store("date");

        // Explicitly track ONLY these 4 core dependencies
        const lat = loc.latitude;
        const lon = loc.longitude;
        const tz = loc.timezone;
        const dt = date.date;

        if (lat !== undefined && lat !== null) {
          this.computeTimings();
          // Update narrations once immediately after re-computing
          this.updateSandhyaKalaTimeNarrations();
        }
      });

      // 3. THE TICKER: Independent of the watcher
      // This updates the "45m left" UI every minute without triggering re-computation
      this.updateSandhyaKalaTimeNarrations();
      setInterval(() => {
        this.updateSandhyaKalaTimeNarrations();
      }, 60 * 1000);
    },

    // watchForChanges() {
    //   Alpine.effect(() => {
    //     waitForAlpineStore("userPreferences").then((store) => {
    //       Alpine.effect(() => {
    //         switch (store.timeFormat) {
    //           case "24h":
    //             this.timeFormat = store.showSeconds ? "HH:mm:ss" : "HH:mm";
    //             break;

    //           case "12h":
    //           default:
    //             this.timeFormat = store.showSeconds ? "hh:mm:ss a" : "hh:mm a";
    //         }
    //       });
    //     });

    //     const loc = Alpine.store("location");
    //     const date = Alpine.store("date");

    //     // if(typeof loc.latitude === 'undefined' || loc.latitude === null) {
    //     //   return;
    //     // }
    //     // Access all reactive properties you want to watch
    //     const _ = [loc.latitude, loc.longitude, loc.timezone, date.date];

    //     // Any change to these properties will rerun this effect
    //     this.computeTimings();
    //     this.updateSandhyaKalaTimeNarrations();
    //   });

    //   this.updateSandhyaKalaTimeNarrations();
    //   setInterval(() => this.updateSandhyaKalaTimeNarrations(), 60 * 1000);
    // },

    get dateJSObject() {
      const tz = Alpine.store("location").timezone || "UTC";
      const dateStr = Alpine.store("date").date;

      // The "Noon Buffer" ensures Local Day == UTC Day for all global timezones
      return DateTime.fromISO(dateStr, { zone: tz })
        .set({ hour: 12, minute: 0, second: 0 })
        .toJSDate();
    },

    computeTimings() {
      console.log("Compute TImings");
      this._computeSandhyaTimings();
      // this._computeSunriseSunset();
      console.log(this.sandhyaTimings["brahma_muhurta"][0]);
    },

    _computeSandhyaTimings() {
      this._computeSunriseSunset();

      if (
        !(this.solarTimings.sunrise.isValid && this.solarTimings.sunset.isValid)
      ) {
        return false;
      }

      // this.sandhyaTimings = {};

      const sandhyaTimings = {
        brahma_muhurta: [
          this.solarTimings.sunrise.minus({ minutes: 96 }),
          this.solarTimings.sunrise.minus({ minutes: 48 }),
        ],
        pratah_sandhyopasanam: {
          uttama: [
            this.solarTimings.sunrise.minus({ minutes: 48 }),
            this.solarTimings.dawn, // start of civil dawm
          ],
          madhyama: [this.solarTimings.dawn, this.solarTimings.sunrise],
          adhama: [
            this.solarTimings.sunrise,
            this.solarTimings.sunrise.plus({ minutes: 72 }),
          ],
          prayaschitta: [
            this.solarTimings.sunrise.plus({ minutes: 72 }),
            this.solarTimings.sunset.plus({ minutes: 180 }),
          ],
        },

        madhyahnika_sandhyopasanam: {
          uttama: [
            this.solarTimings.solarNoon.minus({ minutes: 48 }),
            this.solarTimings.solarNoon,
          ],

          madhyama: [
            this.solarTimings.solarNoon
              .minus({ minutes: 48 })
              .diff(this.solarTimings.sunrise.plus({ minutes: 264 }), "minutes")
              .minutes >= 24
              ? this.solarTimings.sunrise.plus({ minutes: 264 })
              : this.solarTimings.solarNoon.minus({ minutes: 96 }),
            this.solarTimings.solarNoon.minus({ minutes: 48 }),
          ],

          adhama: [
            this.solarTimings.solarNoon,
            this.solarTimings.solarNoon.plus({ minutes: 72 }),
          ],

          prayaschitta: [
            this.solarTimings.solarNoon.plus({ minutes: 72 }),
            this.solarTimings.sunset.plus({ minutes: 180 }),
          ],
        },

        sayam_sandhyopasanam: {
          uttama: [
            this.solarTimings.sunset.minus({ minutes: 48 }),
            this.solarTimings.sunset,
          ],

          madhyama: [this.solarTimings.sunset, this.solarTimings.nauticalDusk],

          adhama: [
            this.solarTimings.nauticalDusk,
            this.solarTimings.sunset.plus({ minutes: 72 }),
          ],

          prayaschitta: [
            this.solarTimings.sunset.plus({ minutes: 72 }),
            this.solarTimings.sunset.plus({ minutes: 180 }),
          ],
        },

        // Proforma - need to properly verify agaist Smriti Muktaphalam, Smriti Chandrika and Nirnaya Sindhu for consensus
        pratah_agnihotram: {
          uttama: [
            this.solarTimings.sunrise,
            this.solarTimings.sunrise.plus({ minutes: 48 }),
          ],
          gauna: [
            this.solarTimings.suryodayah.plus({ minutes: 48 }),
            this.solarTimings.suryodayah.plus({ minutes: 48 * 2 }),
          ],
        },

        sayam_agnihotram: {
          uttama: [
            this.solarTimings.suryastah,
            this.solarTimings.suryastah.plus({ minutes: 48 }),
          ],
          gauna: [
            this.solarTimings.suryastah.plus({ minutes: 48 }),
            this.solarTimings.suryastah.plus({ minutes: 48 * 2 }),
          ],
        },

        // more ...
      };
      this.sandhyaTimings = Object.freeze(sandhyaTimings);
    },

    formatTime(dt) {
      if (!dt || !dt.isValid) return "Invalid"; // Safety check

      // Example: this.timeFormat could be "HH:mm a" or "HH:mm:ss"
      return dt.toFormat(this.timeFormat);
    },

    _computeSunriseSunset: function () {
      // First, add the custom time definition to SunCalc:
      // 0 degrees corresponds to the center of the sun at the geometric horizon
      SunCalc.addTime(0, "suryodayah", "suryastah");

      const suncalc = SunCalc.getTimes(
        this.dateJSObject,
        Alpine.store("location").latitude,
        Alpine.store("location").longitude,
        Alpine.store("location").altitude ?? 0,
      );

      console.log("Altitude " + Alpine.store("location").altitude);

      // this.solarTimings = {};
      let solarTimings = {};
      for (const [key, value] of Object.entries(suncalc)) {
        // this.solarTimings[key] = DateTime.fromJSDate(value); // interpret as UTC
        // //.setZone(Alpine.store("location").timezone); // convert to local
        solarTimings[key] = DateTime.fromJSDate(value).setZone(
          Alpine.store("location").timezone,
        ); // convert to local
      }

      this.solarTimings = Object.freeze(solarTimings);
      return;
    },

    isNow(startTime, endTime) {
      // 1. Get the target timezone from the startTime object itself
      const targetZone = startTime.zoneName;

      // 2. Normalize 'now' to that specific timezone
      // This ensures 'now' and 'startTime' are on the same calendar page
      const now = DateTime.now().setZone(targetZone);

      // Debugging log (Cleaned up)
      // console.log(
      //   JSON.stringify(
      //     {
      //       locationZone: targetZone,
      //       startTime: startTime.toISO(),
      //       nowInTargetZone: now.toISO(),
      //       isSameDay: startTime.hasSame(now, "day"),
      //       isInRange: Interval.fromDateTimes(startTime, endTime).contains(now),
      //     },
      //     null,
      //     2,
      //   ),
      // );

      // 3. Check if it's the same calendar day in the TARGET location
      const isToday = startTime.hasSame(now, "day");
      if (!isToday) return false;

      // 4. Check if the current time falls within the interval
      return Interval.fromDateTimes(startTime, endTime).contains(now);
    },

    minuteDifference(startTime, endTime) {
      if (!startTime || !endTime) return 0;

      if (!startTime?.isValid || !endTime?.isValid) return 0;

      const diffMinutes = endTime.diff(startTime, "minutes").minutes;
      return diffMinutes;
    },

    sandhyaWindowStatus(startTime, endTime) {
      if (!startTime?.isValid || !endTime?.isValid) {
        return { show: false, status: "invalid", diff: 0 };
      }

      const diff = Math.round(
        (endTime.toSeconds() - startTime.toSeconds()) / 60,
      );

      if (diff <= 0) {
        return { show: false, status: "collapsed", diff };
      }

      if (diff > 0 && diff < 15) {
        return { show: true, status: "very-compressed", diff };
      }

      if (diff > 15 && diff < 24) {
        return { show: true, status: "compressed", diff };
      }

      return { show: true, status: "normal", diff };
    },

    formatDuration(minutes) {
      if (minutes < 0) return "";

      const hrs = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);

      if (hrs === 0) {
        return `${mins} minutes`;
      }

      if (mins === 0) {
        return `${hrs} hours`;
      }

      return `${hrs}h ${mins}m`;
    },

    // Add this to your Alpine store (sandhyopasanam)
    getRelativeStatus(startTime, endTime) {
      let now = DateTime.now(); // Get current time in Luxon
      // 1. If it's in the past (now is after endTime)

      // Only show narration if startTime is on today's date
      if (!startTime.hasSame(now, "day")) {
        now = this.solarTimings["sunrise"];

        // post sunrise
        const diff = startTime.diff(now, ["hours", "minutes"]).toObject();

        const hrs = Math.floor(Math.abs(diff.hours));
        const mins = Math.round(Math.abs(diff.minutes));

        let status = "";
        if (hrs > 0) {
          status = mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
        } else {
          status = `${mins}m`;
        }

        if (startTime < now) {
          // pre sunrise
          status += " before ";
        } else {
          status += " after ";
        }

        status += "Sunrise";
        return status;
      }

      if (now > endTime) {
        const prathamaPrahara = this.solarTimings["sunset"].plus({
          minutes: 180,
        });
        if (now < prathamaPrahara) {
          const remaining = prathamaPrahara
            .diff(now, ["hours", "minutes"])
            .toObject();
          const hrs = Math.floor(remaining.hours);
          const mins = Math.round(remaining.minutes);
          
          if(hrs > 2) {
            return 'Prayaschitta valid till '+this.formatTime(prathamaPrahara);
          }

          const narrationPrefix = 'Prayaschitta valid for ';
          if (hrs > 0) {
            return narrationPrefix + (mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`);
          }
          return `${narrationPrefix}${mins}m`;
        }
        return "";
      }
      // 2. If it's active now (now is between start and end)
      if (now >= startTime && now <= endTime) {
        // const total = Math.round(endTime.diff(startTime, "minutes").minutes);
        const remaining = endTime.diff(now, ["hours", "minutes"]).toObject();
        const hrs = Math.floor(remaining.hours);
        const mins = Math.round(remaining.minutes);
        if (hrs > 0) {
          return mins > 0 ? `${hrs}h ${mins}m left` : `${hrs}h left`;
        }
        return `${mins}m left`;
        // return `${remaining}m of ${total}m remaining`;
      }

      // 3. If it's in the future (now is before startTime)
      if (now < startTime) {
        const diff = startTime.diff(now, ["hours", "minutes"]).toObject();
        const hrs = Math.floor(diff.hours);
        const mins = Math.round(diff.minutes);

        if (hrs > 0) {
          return mins > 0 ? `Starts in ${hrs}h ${mins}m` : `Starts in ${hrs}h`;
        }
        return `Starts in ${mins}m`;
      }

      return "";
    },

    updateSandhyaKalaTimeNarrations() {
      let now = DateTime.now(); // Get current time in Luxon

      if (!this.solarTimings || !this.solarTimings["sunrise"]) {
        return;
      }
      let tempObject = {};

      for (const key of Object.keys(this.sandhyaKalaTimeNarration)) {
        const value = this.sandhyaTimings[key];

        let startValue = "";
        if (key === "madhyahnika_sandhyopasanam") {
          startValue = "madhyama";
        } else {
          startValue = "uttama";
        }
        tempObject[key] = {
          percentage: this.getProgress(
            value[startValue][0],
            value["adhama"][1],
          ),
          narration: this.getRelativeStatus(
            value[startValue][0],
            value["adhama"][1],
          ),
          progressColor: this.getProgressColor(value),
          activeKala: this.getActiveKala(value),
        };
      }

      this.sandhyaKalaTimeNarration = Object.freeze(tempObject);
    },

    getActiveKala(sandhyaKala) {
      let now = DateTime.now();
      for (const k of Object.keys(sandhyaKala)) {
        if (k === "prayaschitta") {
          continue;
        }
        if (now >= sandhyaKala[k][0] && now <= sandhyaKala[k][1]) {
          return k;
        }
      }

      return null;
    },

    getProgress(startTime, endTime) {
      const now = DateTime.now(); // Get current time in Luxon

      // 2. If it's active now (now is between start and end)
      if (now >= startTime && now <= endTime) {
        // const total = Math.round(endTime.diff(startTime, "minutes").minutes);
        const remaining = Math.round(endTime.diff(now, "minutes").minutes);

        const window = endTime.toSeconds() - startTime.toSeconds();
        const completed = now.toSeconds() - startTime.toSeconds();
        const percentage = (completed / window) * 100;

        return Math.ceil(percentage);
      }

      return 0;
    },

    getProgressColor(sandhya) {
      const now = DateTime.now(); // Get current time in Luxon

      if (now >= sandhya["uttama"][0] && now <= sandhya["uttama"][1]) {
        return "bg-success";
      }

      if (now >= sandhya["madhyama"][0] && now <= sandhya["madhyama"][1]) {
        return "bg-primary";
      }

      if (now >= sandhya["adhama"][0] && now <= sandhya["adhama"][1]) {
        const start = sandhya["adhama"][0];
        const end = sandhya["adhama"][1];

        // 1. Get difference in minutes, 2. divide by 2
        const halfDiffMinutes = end.diff(start, "minutes").minutes / 2;

        // 3. Add it to value[0] (the start time)
        const midpoint = start.plus({ minutes: halfDiffMinutes });

        // 4. Check if now is past that or not
        if (now >= midpoint) {
          return "bg-danger";
        } else {
          return "bg-warning";
        }
      }

      return "";
    },
    // Specifically for testing
  };
}
